import { useState, useEffect, useCallback } from "react";
import { PageHeader } from "../components/PageHeader";
import { useAuthStore } from "../stores/authStore";
import { Navigate } from "react-router-dom";
import {
  listAdminSubscriptions,
  getAdminSubscriptionStats,
  getAdminRevenue,
  listAdminOverrides,
  listAdminAuditLogs,
  compUser,
  pauseSubscription,
  resumeSubscription,
  extendSubscription,
  forceCancelSubscription,
  revokeOverride,
  type AdminSubscription,
  type SubscriptionStats,
  type RevenueBreakdown,
  type SubscriptionOverride,
  type SubscriptionAuditLogEntry,
  type SubscriptionStatus,
  type AuditActorType,
} from "../services/adminService";

// ==========================================
// Helpers
// ==========================================

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString();
}

function formatRelativeTime(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function daysUntil(dateStr: string): number {
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86400000));
}

// ==========================================
// Status Badge
// ==========================================

const STATUS_STYLES: Record<string, string> = {
  active: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  expiring: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  renewing: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  payment_failed: "bg-red-500/20 text-red-400 border-red-500/30",
  suspended: "bg-red-500/20 text-red-400 border-red-500/30",
  canceled: "bg-surface-600/50 text-surface-400 border-surface-500/30",
  paused: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  comped: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  none: "bg-surface-700/50 text-surface-500 border-surface-600/30",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLES[status] || STATUS_STYLES.none}`}
    >
      {status.replace("_", " ")}
    </span>
  );
}

// ==========================================
// Audit Action Badge
// ==========================================

const ACTION_COLORS: Record<string, string> = {
  "subscription.": "bg-blue-500/20 text-blue-400",
  "payment.": "bg-emerald-500/20 text-emerald-400",
  "payment_method.": "bg-cyan-500/20 text-cyan-400",
  "referral.": "bg-purple-500/20 text-purple-400",
  "override.": "bg-amber-500/20 text-amber-400",
  "agreement.": "bg-surface-600 text-surface-300",
};

function getActionColor(action: string): string {
  for (const [prefix, cls] of Object.entries(ACTION_COLORS)) {
    if (action.startsWith(prefix)) return cls;
  }
  return "bg-surface-600 text-surface-300";
}

const ACTOR_COLORS: Record<string, string> = {
  user: "text-blue-400",
  admin: "text-amber-400",
  system: "text-surface-400",
  stripe_webhook: "text-purple-400",
};

// ==========================================
// KPI Card
// ==========================================

function KPICard({
  label,
  value,
  subValue,
  icon,
  color = "primary",
}: {
  label: string;
  value: string | number;
  subValue?: string;
  icon: string;
  color?: string;
}) {
  const colorClasses: Record<string, string> = {
    primary: "from-primary-500/20 to-primary-500/5 border-primary-500/30",
    emerald: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/30",
    amber: "from-amber-500/20 to-amber-500/5 border-amber-500/30",
    red: "from-red-500/20 to-red-500/5 border-red-500/30",
    purple: "from-purple-500/20 to-purple-500/5 border-purple-500/30",
    blue: "from-blue-500/20 to-blue-500/5 border-blue-500/30",
  };

  return (
    <div
      className={`bg-gradient-to-br ${colorClasses[color] || colorClasses.primary} border rounded-xl p-4`}
    >
      <div className="flex items-center gap-3">
        <div className="text-2xl">{icon}</div>
        <div>
          <p className="text-xs text-surface-400 uppercase tracking-wider">
            {label}
          </p>
          <p className="text-2xl font-bold text-surface-100">{value}</p>
          {subValue && (
            <p className="text-xs text-surface-400 mt-0.5">{subValue}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// Action Modal
// ==========================================

type ActionType = "comp" | "pause" | "resume" | "extend" | "cancel" | null;

function ActionModal({
  action,
  sub,
  onClose,
  onSuccess,
}: {
  action: ActionType;
  sub: AdminSubscription | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [reason, setReason] = useState("");
  const [days, setDays] = useState(30);
  const [expiresAt, setExpiresAt] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!action || !sub) return null;

  const titles: Record<string, string> = {
    comp: "Comp User — Free Access",
    pause: "Pause Subscription",
    resume: "Resume Subscription",
    extend: "Extend Subscription",
    cancel: "Force Cancel Subscription",
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      switch (action) {
        case "comp":
          await compUser(sub.user_id, {
            reason,
            expires_at: expiresAt || undefined,
            notes: notes || undefined,
          });
          break;
        case "pause":
          await pauseSubscription(sub.user_id, reason);
          break;
        case "resume":
          await resumeSubscription(sub.user_id);
          break;
        case "extend":
          await extendSubscription(sub.user_id, { days, reason });
          break;
        case "cancel":
          await forceCancelSubscription(sub.user_id, reason);
          break;
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const needsReason = action !== "resume";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-surface-900 border border-surface-700/50 rounded-2xl p-6 w-full max-w-md shadow-2xl">
        <h3 className="text-lg font-semibold text-surface-100 mb-1">
          {titles[action]}
        </h3>
        <p className="text-sm text-surface-400 mb-4">
          {sub.user_display_name} ({sub.user_email})
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
            {error}
          </div>
        )}

        {needsReason && (
          <div className="mb-4">
            <label className="block text-xs text-surface-400 mb-1">
              Reason *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Enter reason (min 10 chars)..."
              className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-200 focus:outline-none focus:border-primary-500/50"
            />
          </div>
        )}

        {action === "extend" && (
          <div className="mb-4">
            <label className="block text-xs text-surface-400 mb-1">
              Days to extend
            </label>
            <input
              type="number"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value) || 0)}
              min={1}
              max={365}
              className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-200 focus:outline-none focus:border-primary-500/50"
            />
          </div>
        )}

        {action === "comp" && (
          <>
            <div className="mb-4">
              <label className="block text-xs text-surface-400 mb-1">
                Expires at (optional)
              </label>
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-200 focus:outline-none focus:border-primary-500/50"
              />
            </div>
            <div className="mb-4">
              <label className="block text-xs text-surface-400 mb-1">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Internal notes..."
                className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-200 focus:outline-none focus:border-primary-500/50"
              />
            </div>
          </>
        )}

        {action === "cancel" && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400">
            This will immediately cancel the subscription. The user will lose
            access right away. This cannot be undone.
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg bg-surface-800 hover:bg-surface-700 text-surface-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || (needsReason && reason.length < 10)}
            className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              action === "cancel"
                ? "bg-red-500 hover:bg-red-600 text-white"
                : "bg-primary-500 hover:bg-primary-600 text-white"
            }`}
          >
            {isSubmitting ? "Processing..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// Tabs
// ==========================================

type TabId = "overview" | "subscriptions" | "overrides" | "audit";

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: "overview", label: "Dashboard", icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" },
  { id: "subscriptions", label: "Subscriptions", icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" },
  { id: "overrides", label: "Overrides", icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" },
  { id: "audit", label: "Activity Feed", icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" },
];

// ==========================================
// Main Page
// ==========================================

export function AdminSubscriptionsPage() {
  const { user } = useAuthStore();
  const isUserAdmin = user?.roles?.some((r) => r.name === "TradeCraft_Admin");

  // Tab state
  const [activeTab, setActiveTab] = useState<TabId>("overview");

  // Dashboard state
  const [stats, setStats] = useState<SubscriptionStats | null>(null);
  const [revenue, setRevenue] = useState<RevenueBreakdown | null>(null);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(true);

  // Subscriptions state
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [subsTotal, setSubsTotal] = useState(0);
  const [subsPage, setSubsPage] = useState(0);
  const [subsSearch, setSubsSearch] = useState("");
  const [subsStatusFilter, setSubsStatusFilter] = useState<SubscriptionStatus | "">("");
  const [isLoadingSubs, setIsLoadingSubs] = useState(false);

  // Overrides state
  const [overrides, setOverrides] = useState<SubscriptionOverride[]>([]);
  const [overridesTotal, setOverridesTotal] = useState(0);
  const [overridesActiveOnly, setOverridesActiveOnly] = useState(true);
  const [isLoadingOverrides, setIsLoadingOverrides] = useState(false);

  // Audit log state
  const [auditLogs, setAuditLogs] = useState<SubscriptionAuditLogEntry[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(0);
  const [auditActionFilter, setAuditActionFilter] = useState("");
  const [auditActorFilter, setAuditActorFilter] = useState<AuditActorType | "">("");
  const [auditUserFilter, setAuditUserFilter] = useState("");
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);

  // Action modal state
  const [actionType, setActionType] = useState<ActionType>(null);
  const [actionSub, setActionSub] = useState<AdminSubscription | null>(null);

  // Global error
  const [error, setError] = useState<string | null>(null);
  // Success toast
  const [toast, setToast] = useState<string | null>(null);

  // ==========================================
  // Data Fetching
  // ==========================================

  const fetchDashboard = useCallback(async () => {
    setIsLoadingDashboard(true);
    try {
      const [statsData, revenueData] = await Promise.all([
        getAdminSubscriptionStats(),
        getAdminRevenue(),
      ]);
      setStats(statsData);
      setRevenue(revenueData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load dashboard");
    } finally {
      setIsLoadingDashboard(false);
    }
  }, []);

  const fetchSubscriptions = useCallback(async () => {
    setIsLoadingSubs(true);
    try {
      const data = await listAdminSubscriptions(
        subsStatusFilter || undefined,
        subsSearch || undefined,
        20,
        subsPage * 20,
      );
      setSubscriptions(data.subscriptions || []);
      setSubsTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load subscriptions");
    } finally {
      setIsLoadingSubs(false);
    }
  }, [subsStatusFilter, subsSearch, subsPage]);

  const fetchOverrides = useCallback(async () => {
    setIsLoadingOverrides(true);
    try {
      const data = await listAdminOverrides(overridesActiveOnly, 50, 0);
      setOverrides(data.overrides || []);
      setOverridesTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load overrides");
    } finally {
      setIsLoadingOverrides(false);
    }
  }, [overridesActiveOnly]);

  const fetchAuditLogs = useCallback(async () => {
    setIsLoadingAudit(true);
    try {
      const data = await listAdminAuditLogs(
        auditActionFilter || undefined,
        (auditActorFilter as AuditActorType) || undefined,
        auditUserFilter || undefined,
        25,
        auditPage * 25,
      );
      setAuditLogs(data.audit_logs || []);
      setAuditTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load audit logs");
    } finally {
      setIsLoadingAudit(false);
    }
  }, [auditActionFilter, auditActorFilter, auditUserFilter, auditPage]);

  // Load data on mount & tab
  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (activeTab === "subscriptions") fetchSubscriptions();
  }, [activeTab, fetchSubscriptions]);

  useEffect(() => {
    if (activeTab === "overrides") fetchOverrides();
  }, [activeTab, fetchOverrides]);

  useEffect(() => {
    if (activeTab === "audit") fetchAuditLogs();
  }, [activeTab, fetchAuditLogs]);

  // Toast auto-dismiss
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  // Handle action success
  const handleActionSuccess = () => {
    setToast("Action completed successfully");
    fetchDashboard();
    fetchSubscriptions();
  };

  // Handle override revoke
  const handleRevokeOverride = async (overrideId: string) => {
    if (!confirm("Revoke this override? The user will lose their access override."))
      return;
    try {
      await revokeOverride(overrideId);
      setToast("Override revoked");
      fetchOverrides();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to revoke override");
    }
  };

  // Auth guard
  if (!isUserAdmin) {
    return <Navigate to="/" replace />;
  }

  // ==========================================
  // Compute derived stats
  // ==========================================

  const totalSubs = stats
    ? Object.values(stats).reduce((a, b) => a + b, 0)
    : 0;
  const activeSubs = stats
    ? (stats.active || 0) + (stats.expiring || 0) + (stats.renewing || 0) + (stats.comped || 0)
    : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      <PageHeader
        title="Subscription Management"
        subtitle="Full visibility into subscriptions, revenue, overrides, and activity"
      />

      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 px-4 py-3 rounded-xl text-sm shadow-lg">
          {toast}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            &times;
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-surface-800/50 rounded-xl p-1 border border-surface-700/30">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? "bg-primary-500/20 text-primary-400 border border-primary-500/30"
                : "text-surface-400 hover:text-surface-200 hover:bg-surface-700/50 border border-transparent"
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
            </svg>
            {tab.label}
          </button>
        ))}
      </div>

      {/* =========================== DASHBOARD TAB =========================== */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {isLoadingDashboard ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
            </div>
          ) : (
            <>
              {/* Revenue KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <KPICard
                  label="MRR"
                  value={revenue ? formatCents(revenue.mrr_cents) : "$0"}
                  subValue="Monthly recurring"
                  icon="📈"
                  color="emerald"
                />
                <KPICard
                  label="Total Revenue"
                  value={revenue ? formatCents(revenue.total_revenue_cents) : "$0"}
                  subValue="All time"
                  icon="💰"
                  color="primary"
                />
                <KPICard
                  label="Last 30 Days"
                  value={revenue ? formatCents(revenue.monthly_revenue_cents) : "$0"}
                  subValue="Revenue"
                  icon="📊"
                  color="blue"
                />
                <KPICard
                  label="Active"
                  value={activeSubs}
                  subValue={`of ${totalSubs} total`}
                  icon="✅"
                  color="emerald"
                />
                <KPICard
                  label="Churned (30d)"
                  value={revenue?.churned_last_30_days ?? 0}
                  subValue="Cancellations"
                  icon="📉"
                  color="red"
                />
                <KPICard
                  label="Comped"
                  value={stats?.comped ?? 0}
                  subValue="Free access"
                  icon="🎁"
                  color="purple"
                />
              </div>

              {/* Status breakdown */}
              <div className="bg-surface-800/30 border border-surface-700/30 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-surface-200 mb-4">
                  Subscription Status Breakdown
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                  {stats &&
                    Object.entries(stats)
                      .sort(([, a], [, b]) => b - a)
                      .map(([status, count]) => (
                        <button
                          key={status}
                          onClick={() => {
                            setSubsStatusFilter(status as SubscriptionStatus);
                            setActiveTab("subscriptions");
                          }}
                          className="bg-surface-800/50 border border-surface-700/30 rounded-lg p-3 text-center hover:border-primary-500/30 transition-colors"
                        >
                          <StatusBadge status={status} />
                          <p className="text-xl font-bold text-surface-100 mt-2">
                            {count}
                          </p>
                        </button>
                      ))}
                </div>
              </div>

              {/* Quick actions */}
              <div className="bg-surface-800/30 border border-surface-700/30 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-surface-200 mb-4">
                  Quick Navigation
                </h3>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setActiveTab("subscriptions")}
                    className="px-4 py-2 text-sm bg-surface-800 border border-surface-700/30 rounded-lg text-surface-300 hover:text-surface-100 hover:border-primary-500/30 transition-colors"
                  >
                    View All Subscriptions
                  </button>
                  <button
                    onClick={() => {
                      setSubsStatusFilter("payment_failed");
                      setActiveTab("subscriptions");
                    }}
                    className="px-4 py-2 text-sm bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 hover:bg-red-500/20 transition-colors"
                  >
                    Payment Failed ({stats?.payment_failed ?? 0})
                  </button>
                  <button
                    onClick={() => {
                      setSubsStatusFilter("paused");
                      setActiveTab("subscriptions");
                    }}
                    className="px-4 py-2 text-sm bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-yellow-400 hover:bg-yellow-500/20 transition-colors"
                  >
                    Paused ({stats?.paused ?? 0})
                  </button>
                  <button
                    onClick={() => setActiveTab("overrides")}
                    className="px-4 py-2 text-sm bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-400 hover:bg-amber-500/20 transition-colors"
                  >
                    View Overrides
                  </button>
                  <button
                    onClick={() => setActiveTab("audit")}
                    className="px-4 py-2 text-sm bg-blue-500/10 border border-blue-500/20 rounded-lg text-blue-400 hover:bg-blue-500/20 transition-colors"
                  >
                    Activity Feed
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* =========================== SUBSCRIPTIONS TAB =========================== */}
      {activeTab === "subscriptions" && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <input
              type="text"
              placeholder="Search by email..."
              value={subsSearch}
              onChange={(e) => {
                setSubsSearch(e.target.value);
                setSubsPage(0);
              }}
              className="bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-200 w-64 focus:outline-none focus:border-primary-500/50"
            />
            <select
              value={subsStatusFilter}
              onChange={(e) => {
                setSubsStatusFilter(e.target.value as SubscriptionStatus | "");
                setSubsPage(0);
              }}
              className="bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-200 focus:outline-none focus:border-primary-500/50"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="expiring">Expiring</option>
              <option value="renewing">Renewing</option>
              <option value="payment_failed">Payment Failed</option>
              <option value="suspended">Suspended</option>
              <option value="canceled">Canceled</option>
              <option value="paused">Paused</option>
              <option value="comped">Comped</option>
            </select>
            <button
              onClick={() => {
                setSubsSearch("");
                setSubsStatusFilter("");
                setSubsPage(0);
              }}
              className="text-xs text-surface-400 hover:text-surface-200"
            >
              Clear filters
            </button>
            <span className="text-xs text-surface-500 ml-auto">
              {subsTotal} result{subsTotal !== 1 && "s"}
            </span>
          </div>

          {/* Table */}
          {isLoadingSubs ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="text-center py-12 text-surface-500">
              No subscriptions found
            </div>
          ) : (
            <div className="bg-surface-800/30 border border-surface-700/30 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-surface-700/30">
                      <th className="text-left px-4 py-3 text-xs text-surface-400 font-medium uppercase tracking-wider">
                        User
                      </th>
                      <th className="text-left px-4 py-3 text-xs text-surface-400 font-medium uppercase tracking-wider">
                        Status
                      </th>
                      <th className="text-left px-4 py-3 text-xs text-surface-400 font-medium uppercase tracking-wider">
                        Plan
                      </th>
                      <th className="text-left px-4 py-3 text-xs text-surface-400 font-medium uppercase tracking-wider">
                        Period End
                      </th>
                      <th className="text-left px-4 py-3 text-xs text-surface-400 font-medium uppercase tracking-wider">
                        Created
                      </th>
                      <th className="text-right px-4 py-3 text-xs text-surface-400 font-medium uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-700/20">
                    {subscriptions.map((sub) => (
                      <tr
                        key={sub.id}
                        className="hover:bg-surface-800/50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-surface-200 font-medium">
                              {sub.user_display_name}
                            </p>
                            <p className="text-xs text-surface-500">
                              {sub.user_email}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={sub.status} />
                          {sub.cancel_at_period_end && (
                            <span className="block text-xs text-amber-400 mt-1">
                              Cancels at period end
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-surface-200">
                            {sub.plan_display_name}
                          </p>
                          <p className="text-xs text-surface-500">
                            {formatCents(sub.plan_price_cents)}/yr
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          {sub.current_period_end ? (
                            <div>
                              <p className="text-surface-300">
                                {new Date(sub.current_period_end).toLocaleDateString()}
                              </p>
                              <p className="text-xs text-surface-500">
                                {daysUntil(sub.current_period_end)} days left
                              </p>
                            </div>
                          ) : (
                            <span className="text-surface-600">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-surface-400">
                          {formatRelativeTime(sub.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 justify-end">
                            {sub.status === "active" && (
                              <>
                                <ActionButton
                                  label="Pause"
                                  color="yellow"
                                  onClick={() => {
                                    setActionType("pause");
                                    setActionSub(sub);
                                  }}
                                />
                                <ActionButton
                                  label="Extend"
                                  color="blue"
                                  onClick={() => {
                                    setActionType("extend");
                                    setActionSub(sub);
                                  }}
                                />
                              </>
                            )}
                            {sub.status === "paused" && (
                              <ActionButton
                                label="Resume"
                                color="emerald"
                                onClick={() => {
                                  setActionType("resume");
                                  setActionSub(sub);
                                }}
                              />
                            )}
                            {sub.status !== "canceled" && sub.status !== "comped" && (
                              <ActionButton
                                label="Cancel"
                                color="red"
                                onClick={() => {
                                  setActionType("cancel");
                                  setActionSub(sub);
                                }}
                              />
                            )}
                            {sub.status !== "comped" && (
                              <ActionButton
                                label="Comp"
                                color="purple"
                                onClick={() => {
                                  setActionType("comp");
                                  setActionSub(sub);
                                }}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {subsTotal > 20 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-surface-700/30">
                  <button
                    onClick={() => setSubsPage((p) => Math.max(0, p - 1))}
                    disabled={subsPage === 0}
                    className="px-3 py-1.5 text-xs rounded-lg bg-surface-800 border border-surface-700/30 text-surface-400 hover:text-surface-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-xs text-surface-500">
                    Page {subsPage + 1} of {Math.ceil(subsTotal / 20)}
                  </span>
                  <button
                    onClick={() => setSubsPage((p) => p + 1)}
                    disabled={(subsPage + 1) * 20 >= subsTotal}
                    className="px-3 py-1.5 text-xs rounded-lg bg-surface-800 border border-surface-700/30 text-surface-400 hover:text-surface-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* =========================== OVERRIDES TAB =========================== */}
      {activeTab === "overrides" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-surface-300">
              <input
                type="checkbox"
                checked={overridesActiveOnly}
                onChange={(e) => setOverridesActiveOnly(e.target.checked)}
                className="rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
              />
              Active only
            </label>
            <span className="text-xs text-surface-500 ml-auto">
              {overridesTotal} override{overridesTotal !== 1 && "s"}
            </span>
          </div>

          {isLoadingOverrides ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
            </div>
          ) : overrides.length === 0 ? (
            <div className="text-center py-12 text-surface-500">
              No overrides found
            </div>
          ) : (
            <div className="space-y-3">
              {overrides.map((override) => (
                <div
                  key={override.id}
                  className="bg-surface-800/30 border border-surface-700/30 rounded-xl p-4 flex items-center gap-4"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-surface-200 font-medium">
                        {override.user_display_name}
                      </span>
                      <span className="text-xs text-surface-500">
                        {override.user_email}
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          override.is_active
                            ? "bg-emerald-500/20 text-emerald-400"
                            : "bg-surface-600 text-surface-400"
                        }`}
                      >
                        {override.is_active ? "Active" : "Revoked"}
                      </span>
                    </div>
                    <p className="text-xs text-surface-400">
                      Type: {override.override_type} · Reason:{" "}
                      {override.reason}
                    </p>
                    <p className="text-xs text-surface-500">
                      Created {formatRelativeTime(override.created_at)}
                      {override.expires_at &&
                        ` · Expires ${new Date(override.expires_at).toLocaleDateString()}`}
                    </p>
                    {override.notes && (
                      <p className="text-xs text-surface-500 italic mt-1">
                        Notes: {override.notes}
                      </p>
                    )}
                  </div>
                  {override.is_active && (
                    <button
                      onClick={() => handleRevokeOverride(override.id)}
                      className="px-3 py-1.5 text-xs rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors"
                    >
                      Revoke
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* =========================== AUDIT LOG TAB =========================== */}
      {activeTab === "audit" && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <select
              value={auditActionFilter}
              onChange={(e) => {
                setAuditActionFilter(e.target.value);
                setAuditPage(0);
              }}
              className="bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-200 focus:outline-none focus:border-primary-500/50"
            >
              <option value="">All Actions</option>
              <optgroup label="Subscription">
                <option value="subscription.created">Created</option>
                <option value="subscription.activated">Activated</option>
                <option value="subscription.canceled">Canceled</option>
                <option value="subscription.comped">Comped</option>
                <option value="subscription.paused">Paused</option>
                <option value="subscription.resumed">Resumed</option>
                <option value="subscription.expired">Expired</option>
                <option value="subscription.suspended">Suspended</option>
                <option value="subscription.reactivated">Reactivated</option>
              </optgroup>
              <optgroup label="Payment">
                <option value="payment.succeeded">Payment Succeeded</option>
                <option value="payment.failed">Payment Failed</option>
                <option value="payment.refunded">Refunded</option>
              </optgroup>
              <optgroup label="Referral">
                <option value="referral.code_created">Code Created</option>
                <option value="referral.redeemed">Redeemed</option>
                <option value="referral.credit_applied">Credit Applied</option>
                <option value="referral.credit_earned">Credit Earned</option>
              </optgroup>
              <optgroup label="Override">
                <option value="override.granted">Granted</option>
                <option value="override.revoked">Revoked</option>
              </optgroup>
              <optgroup label="Other">
                <option value="payment_method.added">Payment Method Added</option>
                <option value="payment_method.removed">Payment Method Removed</option>
                <option value="agreement.signed">Agreement Signed</option>
              </optgroup>
            </select>
            <select
              value={auditActorFilter}
              onChange={(e) => {
                setAuditActorFilter(e.target.value as AuditActorType | "");
                setAuditPage(0);
              }}
              className="bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-200 focus:outline-none focus:border-primary-500/50"
            >
              <option value="">All Actors</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="system">System</option>
              <option value="stripe_webhook">Stripe Webhook</option>
            </select>
            <input
              type="text"
              placeholder="Filter by user email or ID..."
              value={auditUserFilter}
              onChange={(e) => {
                setAuditUserFilter(e.target.value);
                setAuditPage(0);
              }}
              className="bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-200 w-56 focus:outline-none focus:border-primary-500/50"
            />
            <button
              onClick={() => {
                setAuditActionFilter("");
                setAuditActorFilter("");
                setAuditUserFilter("");
                setAuditPage(0);
              }}
              className="text-xs text-surface-400 hover:text-surface-200"
            >
              Clear
            </button>
            <span className="text-xs text-surface-500 ml-auto">
              {auditTotal} event{auditTotal !== 1 && "s"}
            </span>
          </div>

          {/* Audit list */}
          {isLoadingAudit ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="text-center py-12 text-surface-500">
              No audit events found
            </div>
          ) : (
            <div className="space-y-2">
              {auditLogs.map((log) => (
                <AuditLogRow key={log.id} log={log} />
              ))}

              {/* Pagination */}
              {auditTotal > 25 && (
                <div className="flex items-center justify-between pt-3">
                  <button
                    onClick={() => setAuditPage((p) => Math.max(0, p - 1))}
                    disabled={auditPage === 0}
                    className="px-3 py-1.5 text-xs rounded-lg bg-surface-800 border border-surface-700/30 text-surface-400 hover:text-surface-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-xs text-surface-500">
                    Page {auditPage + 1} of {Math.ceil(auditTotal / 25)}
                  </span>
                  <button
                    onClick={() => setAuditPage((p) => p + 1)}
                    disabled={(auditPage + 1) * 25 >= auditTotal}
                    className="px-3 py-1.5 text-xs rounded-lg bg-surface-800 border border-surface-700/30 text-surface-400 hover:text-surface-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action Modal */}
      <ActionModal
        action={actionType}
        sub={actionSub}
        onClose={() => {
          setActionType(null);
          setActionSub(null);
        }}
        onSuccess={handleActionSuccess}
      />
    </div>
  );
}

// ==========================================
// Small Components
// ==========================================

function ActionButton({
  label,
  color,
  onClick,
}: {
  label: string;
  color: string;
  onClick: () => void;
}) {
  const colorClass: Record<string, string> = {
    yellow: "bg-yellow-500/10 border-yellow-500/20 text-yellow-400 hover:bg-yellow-500/20",
    blue: "bg-blue-500/10 border-blue-500/20 text-blue-400 hover:bg-blue-500/20",
    emerald: "bg-emerald-500/10 border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20",
    red: "bg-red-500/10 border-red-500/20 text-red-400 hover:bg-red-500/20",
    purple: "bg-purple-500/10 border-purple-500/20 text-purple-400 hover:bg-purple-500/20",
  };

  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${colorClass[color] || colorClass.blue}`}
    >
      {label}
    </button>
  );
}

function AuditLogRow({ log }: { log: SubscriptionAuditLogEntry }) {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="bg-surface-800/30 border border-surface-700/30 rounded-xl p-4">
      <div className="flex items-start gap-3">
        {/* Action dot */}
        <div className="mt-1">
          <div
            className={`w-2.5 h-2.5 rounded-full ${
              log.action.startsWith("payment.failed") || log.action.startsWith("subscription.suspended")
                ? "bg-red-400"
                : log.action.startsWith("payment.succeeded")
                  ? "bg-emerald-400"
                  : log.action.startsWith("subscription.canceled")
                    ? "bg-amber-400"
                    : "bg-blue-400"
            }`}
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getActionColor(log.action)}`}>
              {log.action}
            </span>
            <span className={`text-xs font-medium ${ACTOR_COLORS[log.actor_type] || "text-surface-400"}`}>
              {log.actor_type === "stripe_webhook" ? "Stripe" : log.actor_type}
              {log.actor_email && ` (${log.actor_email})`}
            </span>
            {log.previous_status && log.new_status && (
              <span className="text-xs text-surface-500">
                <StatusBadge status={log.previous_status} /> →{" "}
                <StatusBadge status={log.new_status} />
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-surface-300">
              {log.user_display_name}
            </span>
            <span className="text-xs text-surface-500">{log.user_email}</span>
          </div>
        </div>

        {/* Time & expand */}
        <div className="text-right flex-shrink-0">
          <p className="text-xs text-surface-500">{formatRelativeTime(log.created_at)}</p>
          <p className="text-xs text-surface-600">{formatDate(log.created_at)}</p>
          {log.details && Object.keys(log.details).length > 0 && (
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-xs text-primary-400 hover:text-primary-300 mt-1"
            >
              {showDetails ? "Hide" : "Details"}
            </button>
          )}
        </div>
      </div>

      {/* Details expansion */}
      {showDetails && log.details && (
        <div className="mt-3 ml-5 bg-surface-900/50 border border-surface-700/20 rounded-lg p-3">
          <pre className="text-xs text-surface-400 whitespace-pre-wrap break-all">
            {JSON.stringify(log.details, null, 2)}
          </pre>
          {log.ip_address && (
            <p className="text-xs text-surface-500 mt-2">
              IP: {log.ip_address}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
